/**
 * Image cache using IndexedDB for storing cover art images locally
 * This minimizes network requests by caching image blobs after first download
 */

const DB_NAME = 'songkit-image-cache';
const STORE_NAME = 'image-files';
const DB_VERSION = 1;

interface CachedImage {
  key: string; // Cache key (e.g., "song-123" or "song-123-revision-456")
  blob: Blob;
  cachedAt: number;
  expiresAt: number;
}

class ImageBlobCache {
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;
  private objectUrls = new Map<string, string>(); // Track created object URLs

  /**
   * Initialize IndexedDB
   */
  private async init(): Promise<void> {
    if (this.db) {
      return;
    }

    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('[ImageBlobCache] Error opening IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('[ImageBlobCache] IndexedDB initialized');
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create object store if it doesn't exist
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const objectStore = db.createObjectStore(STORE_NAME, { keyPath: 'key' });
          objectStore.createIndex('expiresAt', 'expiresAt', { unique: false });
          console.log('[ImageBlobCache] Created object store');
        }
      };
    });

    return this.initPromise;
  }

  /**
   * Generate cache key from songId and optional revisionId
   */
  getCacheKey(songId: number, revisionId?: number): string {
    return revisionId ? `song-${songId}-revision-${revisionId}` : `song-${songId}`;
  }

  /**
   * Album cache key (separate namespace from songs/revisions). An optional
   * token (derived from the cover URL) busts the cache when the art changes.
   */
  getAlbumCacheKey(albumId: number, token?: string): string {
    return token ? `album-${albumId}-${token}` : `album-${albumId}`;
  }

  /**
   * Get cached image as an object URL
   */
  async get(songId: number, revisionId?: number): Promise<string | null> {
    return this.getByKey(this.getCacheKey(songId, revisionId));
  }

  /** Get a cached album cover as an object URL */
  async getAlbum(albumId: number, token?: string): Promise<string | null> {
    return this.getByKey(this.getAlbumCacheKey(albumId, token));
  }

  private async getByKey(key: string): Promise<string | null> {
    // Check if we already have an object URL for this key
    const existingUrl = this.objectUrls.get(key);
    if (existingUrl) {
      return existingUrl;
    }

    await this.init();

    if (!this.db) {
      return null;
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(key);

      request.onsuccess = () => {
        const cached = request.result as CachedImage | undefined;

        if (!cached) {
          resolve(null);
          return;
        }

        // Check if expired
        if (cached.expiresAt < Date.now()) {
          console.log('[ImageBlobCache] Cached image expired:', key);
          this.deleteByKey(key); // Clean up expired entry
          resolve(null);
          return;
        }

        // Create object URL from blob
        const objectUrl = URL.createObjectURL(cached.blob);
        this.objectUrls.set(key, objectUrl);
        console.log('[ImageBlobCache] Cache hit for:', key);
        resolve(objectUrl);
      };

      request.onerror = () => {
        console.error('[ImageBlobCache] Error getting cached image:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Store image blob in cache
   */
  async set(songId: number, blob: Blob, revisionId?: number, expiresInHours: number = 168): Promise<string> {
    return this.setByKey(this.getCacheKey(songId, revisionId), blob, expiresInHours);
  }

  /** Store an album cover blob in cache */
  async setAlbum(
    albumId: number,
    blob: Blob,
    expiresInHours: number = 168,
    token?: string
  ): Promise<string> {
    return this.setByKey(this.getAlbumCacheKey(albumId, token), blob, expiresInHours);
  }

  private async setByKey(key: string, blob: Blob, expiresInHours: number): Promise<string> {
    await this.init();

    // Create object URL immediately
    const objectUrl = URL.createObjectURL(blob);
    this.objectUrls.set(key, objectUrl);

    if (!this.db) {
      return objectUrl;
    }

    const cachedImage: CachedImage = {
      key,
      blob,
      cachedAt: Date.now(),
      expiresAt: Date.now() + (expiresInHours * 60 * 60 * 1000), // Default 1 week
    };

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.put(cachedImage);

      request.onsuccess = () => {
        console.log('[ImageBlobCache] Cached image:', key);
        resolve(objectUrl);
      };

      request.onerror = () => {
        console.error('[ImageBlobCache] Error caching image:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Delete cached image
   */
  async delete(songId: number, revisionId?: number): Promise<void> {
    return this.deleteByKey(this.getCacheKey(songId, revisionId));
  }

  private async deleteByKey(key: string): Promise<void> {
    // Revoke object URL if it exists
    const objectUrl = this.objectUrls.get(key);
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
      this.objectUrls.delete(key);
    }

    await this.init();

    if (!this.db) {
      return;
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(key);

      request.onsuccess = () => {
        console.log('[ImageBlobCache] Deleted cached image:', key);
        resolve();
      };

      request.onerror = () => {
        console.error('[ImageBlobCache] Error deleting cached image:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Invalidate cache for a song (and optionally all its revisions)
   */
  async invalidate(songId: number): Promise<void> {
    await this.init();

    if (!this.db) {
      return;
    }

    // First, revoke any object URLs for this song
    for (const [key, url] of this.objectUrls.entries()) {
      if (key.startsWith(`song-${songId}`)) {
        URL.revokeObjectURL(url);
        this.objectUrls.delete(key);
      }
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.openCursor();
      const keysToDelete: string[] = [];

      request.onsuccess = () => {
        const cursor = request.result;

        if (cursor) {
          const cached = cursor.value as CachedImage;
          if (cached.key.startsWith(`song-${songId}`)) {
            keysToDelete.push(cached.key);
            cursor.delete();
          }
          cursor.continue();
        } else {
          console.log('[ImageBlobCache] Invalidated cache for song:', songId, 'Deleted:', keysToDelete.length);
          resolve();
        }
      };

      request.onerror = () => {
        console.error('[ImageBlobCache] Error invalidating cache:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Clear all cached images
   */
  async clear(): Promise<void> {
    // Revoke all object URLs
    for (const url of this.objectUrls.values()) {
      URL.revokeObjectURL(url);
    }
    this.objectUrls.clear();

    await this.init();

    if (!this.db) {
      return;
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.clear();

      request.onsuccess = () => {
        console.log('[ImageBlobCache] Cleared all cached images');
        resolve();
      };

      request.onerror = () => {
        console.error('[ImageBlobCache] Error clearing cache:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Get cache size in bytes
   */
  async getSize(): Promise<number> {
    await this.init();

    if (!this.db) {
      return 0;
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => {
        const items = request.result as CachedImage[];
        const totalSize = items.reduce((sum, item) => sum + item.blob.size, 0);
        resolve(totalSize);
      };

      request.onerror = () => {
        console.error('[ImageBlobCache] Error getting cache size:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Get count of cached items
   */
  async getCount(): Promise<number> {
    await this.init();

    if (!this.db) {
      return 0;
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.count();

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onerror = () => {
        console.error('[ImageBlobCache] Error getting cache count:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Clean up expired entries
   */
  async cleanup(): Promise<void> {
    await this.init();

    if (!this.db) {
      return;
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('expiresAt');
      const now = Date.now();

      const request = index.openCursor();

      request.onsuccess = () => {
        const cursor = request.result;

        if (cursor) {
          const cached = cursor.value as CachedImage;

          if (cached.expiresAt < now) {
            // Revoke object URL if it exists
            const objectUrl = this.objectUrls.get(cached.key);
            if (objectUrl) {
              URL.revokeObjectURL(objectUrl);
              this.objectUrls.delete(cached.key);
            }
            cursor.delete();
            console.log('[ImageBlobCache] Cleaned up expired image:', cached.key);
          }

          cursor.continue();
        } else {
          resolve();
        }
      };

      request.onerror = () => {
        console.error('[ImageBlobCache] Error during cleanup:', request.error);
        reject(request.error);
      };
    });
  }
}

// Export singleton instance
export const imageBlobCache = new ImageBlobCache();

// Run cleanup every hour
if (typeof window !== 'undefined') {
  setInterval(() => {
    imageBlobCache.cleanup();
  }, 60 * 60 * 1000);
}
